/**
 * flight_profile.ts — how a versona's craft actually FLIES a leg (2026-09-20).
 *
 * BYTE-IDENTICAL MIRROR: `client/src/lib/flight_profile.ts` and
 * `server/src/lib/flight_profile.ts` must stay the same file (the
 * `combat_core.ts` law). The server integrates the leg and writes the sparse
 * samples; the client rebuilds the SAME plan from the same row and walks it
 * between polls, so the two never disagree about where a ship is.
 *
 * Before this, a journey was a straight line flown at a constant speed with
 * `altitude = min(cruiseAlt, dFrom × 0.28, dTo × 0.28)`. Three things read as
 * wrong, and all three are fixed here:
 *
 *   1. 0.28 is a 15.6° gradient — a fighter's zoom climb, used for the DESCENT
 *      too. The international standard final approach is 3° and an airliner
 *      descends at 1,500–3,000 ft/min; nothing in the sky comes down at 15.6°.
 *   2. The `min()` puts a HARD CORNER at top-of-climb and top-of-descent. The
 *      craft levelled off in one tick.
 *   3. Constant speed the whole way: it left at full cruise and stopped dead on
 *      arrival, and the ground track was a razor-straight chord.
 *
 * What replaces it, from how real aircraft are actually flown:
 *
 *   · THE TRACK is an airway, not a chord: out of A, a turn onto a cruise leg
 *     offset from the direct line, then a turn back in to B. Corners are
 *     FILLETED — the RNAV fly-by rule, turn anticipation DTA = R·tan(Δθ/2),
 *     with R the radius of a banked turn. Turns sit near the ends; the cruise
 *     is wings-level.
 *   · BANK comes from the turn, not from a yaw error: φ = atan(v²·κ/g),
 *     clamped to 25°, the limit airline autopilots use for passenger comfort.
 *     Roll is rate-limited (2–4 s to roll into 30° ⇒ ~10°/s).
 *   · THE VERTICAL PROFILE climbs, cruises and descends with NO corner: a
 *     smootherstep (zero slope AND zero curvature at both ends — the closed
 *     form of a jerk-limited S-curve), with the lengths chosen so the STEEPEST
 *     point of the descent is exactly the 3° glideslope.
 *   · SPEED is an S-curve too: away from A and down into B, so an arrival is a
 *     settle rather than a stop.
 *   · PITCH follows the climb angle. The hull used to stay level through a
 *     15.6° climb, which is most of why it read as a sliding prop.
 *   · THE NOSE CRABS: heading = track + asin((w/v)·sin(wind − course)), the
 *     wind-correction angle. A craft in a crosswind does not point where it
 *     is going.
 *
 * Everything is a pure function of the leg (Hard Rule #5: no RNG, no clock) —
 * the only per-craft variation comes from `seed`, a hash of the versona id.
 */

/** The international standard final-approach glideslope. Nothing comes down steeper on purpose. */
export const GLIDESLOPE_DEG = 3;
/** A ship leaves harder than an airliner, but nowhere near the 15.6° it used to. */
export const CLIMB_DEG = 8;
/** Airline autopilots cap here for passenger comfort; 25–30° is the normal-operations band. */
export const BANK_MAX_DEG = 25;
/** Two to four seconds to roll into a 30° bank. */
export const ROLL_RATE_DEG_S = 10;
/**
 * Peak longitudinal acceleration (m/s²). An airliner works at 1–3; these are
 * small craft, and 1.2 was measured to make a short hop take 180 % longer and
 * look like it was crawling. 8 m/s² (~0.8 g) keeps every leg within a few per
 * cent of the time it used to take while still reading as a throttle-up.
 */
export const LONG_ACCEL = 8;
/** Arrival speed as a share of cruise — a settle, not a stop. */
export const ARRIVE_SPEED_FRAC = 0.30;
/** Departure speed as a share of cruise. */
export const DEPART_SPEED_FRAC = 0.45;
/** How far the cruise leg may sit off the direct line, as a share of leg length. */
export const AIRWAY_OFFSET_FRAC = 0.06;
/** …and never farther than this (m), so a continental leg does not sail into the next province. */
export const AIRWAY_OFFSET_MAX_M = 1400;
/** Where the two turns sit along the leg (share of the chord). */
export const TURN_OUT_FRAC = 0.26;
export const TURN_IN_FRAC = 0.74;
/** Points baked per filleted corner. */
const FILLET_STEPS = 14;
/** Wind speed as a share of cruise (a deterministic stand-in for the met field). */
const WIND_FRAC = 0.07;
const G = 9.81;
const DEG = Math.PI / 180;

export interface FlightLeg {
  ax: number; az: number; bx: number; bz: number;
  /** Metres above the ground at cruise (0 for a land craft). */
  cruiseAlt: number;
  /** Cruise speed (m/s). */
  cruiseSpeed: number;
  /** 0..1, a pure hash of the versona id: which way the airway bends, and the wind. */
  seed: number;
  /** A land craft keeps its altitude profile flat but still accelerates and turns. */
  air: boolean;
}

/**
 * FNV-1a → 0..1. LOCAL to the mirror on purpose: the seed decides which way the
 * airway bends and where the wind is from, so if the two sides hashed with
 * different helpers they would fly different curves. Keeping it here means the
 * byte-identical file is the whole contract.
 */
export function flightSeed(versonaId: bigint | number | string): number {
  const str = `flight|${versonaId}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13;
  return (h >>> 0) / 0xffffffff;
}

/** A `versona_journey` row as a flight leg — the one place the columns are read. */
export function legFromRow(r: {
  versonaId: bigint | number | string; kind: string;
  fromX: number; fromZ: number; destX: number; destZ: number;
  cruiseAlt: number; speed: number;
}): FlightLeg {
  return {
    ax: r.fromX, az: r.fromZ, bx: r.destX, bz: r.destZ,
    cruiseAlt: r.cruiseAlt, cruiseSpeed: r.speed,
    seed: flightSeed(r.versonaId), air: r.kind === 'air',
  };
}

export interface FlightPlan {
  /** Dense polyline, flattened [x0, z0, x1, z1, …] — straights plus baked fillets. */
  pts: number[];
  /** Cumulative arc length at each vertex. */
  cum: number[];
  /** Heading of each segment (yaw = atan2(dx, dz), the crowd convention). */
  yaws: number[];
  length: number;
  peakAlt: number;
  climbLen: number;
  descentLen: number;
  accelLen: number;
  decelLen: number;
  /** The speed actually held at the top of the profile — below the leg's cruise on a short hop. */
  cruiseSpeed: number;
  v0: number;
  vEnd: number;
  /** Where the wind is from (rad) and how hard, for the crab angle. */
  windFrom: number;
  windSpeed: number;
  air: boolean;
}

export interface FlightPose {
  x: number; z: number;
  /** Metres above the ground. */
  alt: number;
  /** Ground track (rad). */
  track: number;
  /** Where the nose points — track plus the wind-correction angle. */
  yaw: number;
  /** Positive = right wing down. */
  bank: number;
  /** Positive = nose up. */
  pitch: number;
  speed: number;
  done: boolean;
}

/** Zero slope AND zero curvature at both ends — the closed form of a jerk-limited S-curve. */
export function smootherstep(t: number): number {
  const x = t <= 0 ? 0 : t >= 1 ? 1 : t;
  return x * x * x * (x * (x * 6 - 15) + 10);
}
/** The steepest slope of smootherstep, at t = 0.5. Lengths are sized from this so the PEAK gradient is the target. */
export const SMOOTHER_PEAK_SLOPE = 1.875;

/** The radius of a banked turn at this speed: R = v² / (g·tan φ). */
export function turnRadius(speed: number, bankDeg: number = BANK_MAX_DEG): number {
  const t = Math.tan(Math.max(1, bankDeg) * DEG);
  return (speed * speed) / (G * t);
}
/** Turn anticipation — how far before a corner the turn begins (the RNAV fly-by rule). */
export function turnAnticipation(radius: number, turnAngle: number): number {
  return radius * Math.tan(Math.min(Math.abs(turnAngle), Math.PI * 0.8) / 2);
}

/** Quadratic Bézier — a corner fillet that is C1 where it meets both straights. */
function bez(p0: number, p1: number, p2: number, t: number): number {
  const u = 1 - t;
  return u * u * p0 + 2 * u * t * p1 + t * t * p2;
}

/** Round the corner at `c` between the straights coming from `a` and going to `b`. */
function fillet(out: number[], ax: number, az: number, cx: number, cz: number, bx: number, bz: number, radius: number): void {
  const i1x = cx - ax, i1z = cz - az, l1 = Math.hypot(i1x, i1z);
  const i2x = bx - cx, i2z = bz - cz, l2 = Math.hypot(i2x, i2z);
  if (l1 < 1e-6 || l2 < 1e-6) { out.push(cx, cz); return; }
  const u1x = i1x / l1, u1z = i1z / l1, u2x = i2x / l2, u2z = i2z / l2;
  const turn = Math.abs(Math.atan2(u1x * u2z - u1z * u2x, u1x * u2x + u1z * u2z));
  if (turn < 1e-4) { out.push(cx, cz); return; }
  // Never eat more than 45 % of either straight, or two fillets could overlap.
  const d = Math.min(turnAnticipation(radius, turn), l1 * 0.45, l2 * 0.45);
  const sx = cx - u1x * d, sz = cz - u1z * d;
  const ex = cx + u2x * d, ez = cz + u2z * d;
  for (let i = 0; i <= FILLET_STEPS; i++) {
    const t = i / FILLET_STEPS;
    out.push(bez(sx, cx, ex, t), bez(sz, cz, ez, t));
  }
}

/**
 * The airway for this leg: out of A, a turn onto a cruise track offset from the
 * direct line, then a turn back in to B. Two gentle turns near the ends with a
 * wings-level cruise between them — an airway, not a chord.
 */
export function planFlight(leg: FlightLeg): FlightPlan {
  const dx = leg.bx - leg.ax, dz = leg.bz - leg.az;
  const chord = Math.hypot(dx, dz);
  const cruiseSpeed = Math.max(1, leg.cruiseSpeed);
  const pts: number[] = [];
  if (chord < 1e-3) {
    pts.push(leg.ax, leg.az, leg.bx, leg.bz);
  } else {
    const ux = dx / chord, uz = dz / chord;
    const nx = uz, nz = -ux;                       // left normal
    const side = leg.seed < 0.5 ? -1 : 1;
    const radius0 = turnRadius(cruiseSpeed);
    // THE AIRWAY MUST BE FLYABLE. Turn radius grows with the SQUARE of speed, so
    // a fast craft on a short leg cannot bend at all: at 91.5 m/s the radius is
    // 1,830 m, and on a 600 m leg the fillet got squeezed to 45 % of a 156 m
    // segment — a turn far tighter than 25° of bank can hold. `flightAt` clamps
    // the bank, so the hull pivoted without banking, which is exactly the thing
    // this module exists to stop. Solve the offset the geometry actually allows:
    // with the corner at `out` along the course, the fillet needs
    // R·tan(Δθ/2) ≤ 0.45·out, so Δθ ≤ 2·atan(0.45·out/R) and amp ≤ out·tan(Δθ).
    const out = chord * TURN_OUT_FRAC;
    const turnCap = Math.min(2 * Math.atan((0.45 * out) / Math.max(1e-6, radius0)), 40 * DEG);
    const ampFit = out * Math.tan(turnCap);
    const amp = Math.min(chord * AIRWAY_OFFSET_FRAC, AIRWAY_OFFSET_MAX_M, ampFit) * side * (0.55 + Math.abs(leg.seed - 0.5));
    const p1x = leg.ax + ux * chord * TURN_OUT_FRAC + nx * amp;
    const p1z = leg.az + uz * chord * TURN_OUT_FRAC + nz * amp;
    const p2x = leg.ax + ux * chord * TURN_IN_FRAC + nx * amp;
    const p2z = leg.az + uz * chord * TURN_IN_FRAC + nz * amp;
    const radius = radius0;
    pts.push(leg.ax, leg.az);
    fillet(pts, leg.ax, leg.az, p1x, p1z, p2x, p2z, radius);
    fillet(pts, p1x, p1z, p2x, p2z, leg.bx, leg.bz, radius);
    pts.push(leg.bx, leg.bz);
  }

  const n = pts.length / 2;
  const cum: number[] = new Array(n).fill(0);
  const yaws: number[] = new Array(Math.max(1, n - 1)).fill(0);
  let length = 0;
  for (let i = 1; i < n; i++) {
    const sx = pts[i * 2] - pts[(i - 1) * 2], sz = pts[i * 2 + 1] - pts[(i - 1) * 2 + 1];
    const l = Math.hypot(sx, sz);
    yaws[i - 1] = l > 1e-9 ? Math.atan2(sx, sz) : (i > 1 ? yaws[i - 2] : 0);
    length += l;
    cum[i] = length;
  }

  // ── Vertical: sized so the STEEPEST point of each is the real gradient ──
  const cruiseAlt = leg.air ? Math.max(0, leg.cruiseAlt) : 0;
  let climbLen = leg.air ? (cruiseAlt * SMOOTHER_PEAK_SLOPE) / Math.tan(CLIMB_DEG * DEG) : 0;
  let descentLen = leg.air ? (cruiseAlt * SMOOTHER_PEAK_SLOPE) / Math.tan(GLIDESLOPE_DEG * DEG) : 0;
  let peakAlt = cruiseAlt;
  // A short leg cannot hold a full climb and a 3° descent; shrink the whole
  // profile rather than steepen it — the craft simply does not go as high.
  const vertWant = climbLen + descentLen;
  if (vertWant > length && vertWant > 1e-6) {
    const k = length / vertWant;
    climbLen *= k; descentLen *= k; peakAlt *= k;
  }

  // ── Speed: S-curves away from A and down into B ──
  const v0 = cruiseSpeed * DEPART_SPEED_FRAC;
  const vEnd = cruiseSpeed * ARRIVE_SPEED_FRAC;
  const accelMean = LONG_ACCEL / SMOOTHER_PEAK_SLOPE;
  // A SHORT LEG NEVER REACHES CRUISE. Solving for the speed it can actually
  // hold — 2v² = 2·a·L + v0² + vEnd² — gives a proper triangular profile
  // instead of scaling the ramps and keeping a peak the craft could never get
  // to. A 2 km hop is simply flown slower, which is what really happens.
  let peakSpeed = cruiseSpeed;
  let accelLen = ((peakSpeed * peakSpeed) - (v0 * v0)) / (2 * accelMean);
  let decelLen = ((peakSpeed * peakSpeed) - (vEnd * vEnd)) / (2 * accelMean);
  if (accelLen + decelLen > length && length > 1e-6) {
    peakSpeed = Math.max(Math.max(v0, vEnd), Math.sqrt((2 * accelMean * length + v0 * v0 + vEnd * vEnd) / 2));
    accelLen = ((peakSpeed * peakSpeed) - (v0 * v0)) / (2 * accelMean);
    decelLen = ((peakSpeed * peakSpeed) - (vEnd * vEnd)) / (2 * accelMean);
    const over = accelLen + decelLen;
    if (over > length && over > 1e-6) { const k = length / over; accelLen *= k; decelLen *= k; }
  }

  // A deterministic wind, so the nose crabs off the track like a real one.
  const windFrom = leg.seed * Math.PI * 2;
  const windSpeed = cruiseSpeed * WIND_FRAC * (0.4 + leg.seed * 0.6);

  return { pts, cum, yaws, length, peakAlt, climbLen, descentLen, accelLen, decelLen, cruiseSpeed: peakSpeed, v0, vEnd, windFrom, windSpeed, air: leg.air };
}

/**
 * A leg's plan, memoised. `advanceJourney` runs once per in-flight journey per
 * tick — 664 of them on the live world — and building the polyline is 75 % of
 * that work and ~3 allocations a craft. A leg's plan never changes while the
 * leg does not, so it is built once and kept. (The `structure_footprints`
 * path cache, for the same reason.)
 */
const planCache = new Map<string, FlightPlan>();
const PLAN_CACHE_MAX = 4096;

/** The identity of a leg: change any of it and the craft is flying a different flight. */
export function planKeyOf(r: {
  versonaId: bigint | number | string; kind: string;
  fromX: number; fromZ: number; destX: number; destZ: number;
  cruiseAlt: number; speed: number;
}): string {
  return `${r.versonaId}|${r.kind}|${r.fromX}|${r.fromZ}|${r.destX}|${r.destZ}|${r.cruiseAlt}|${r.speed}`;
}

/**
 * The plan for a row, from cache when the leg is unchanged. The returned plan
 * is SHARED — never mutate it.
 */
export function planFor(r: {
  versonaId: bigint | number | string; kind: string;
  fromX: number; fromZ: number; destX: number; destZ: number;
  cruiseAlt: number; speed: number;
}): FlightPlan {
  const key = planKeyOf(r);
  const hit = planCache.get(key);
  if (hit) return hit;
  const plan = planFlight(legFromRow(r));
  // Arrived journeys are deleted, so the churn is bounded by the live set; a
  // flat clear is enough and costs nothing to reason about.
  if (planCache.size >= PLAN_CACHE_MAX) planCache.clear();
  planCache.set(key, plan);
  return plan;
}

/** Ground speed at arc length `s`. */
export function speedAt(p: FlightPlan, s: number): number {
  const up = p.accelLen > 1e-6 ? smootherstep(s / p.accelLen) : 1;
  const inSpeed = p.v0 + (p.cruiseSpeed - p.v0) * up;
  const left = p.length - s;
  const down = p.decelLen > 1e-6 ? smootherstep(left / p.decelLen) : 1;
  const outSpeed = p.vEnd + (p.cruiseSpeed - p.vEnd) * down;
  return Math.max(0.5, Math.min(inSpeed, outSpeed));
}

/** Height above the ground at arc length `s` — no corner at top-of-climb or top-of-descent. */
export function altAt(p: FlightPlan, s: number): number {
  if (!p.air || p.peakAlt <= 0) return 0;
  const up = p.climbLen > 1e-6 ? smootherstep(s / p.climbLen) : 1;
  const down = p.descentLen > 1e-6 ? smootherstep((p.length - s) / p.descentLen) : 1;
  return p.peakAlt * Math.min(up, down);
}

/** The vertex index whose segment contains `s`. */
function segAt(p: FlightPlan, s: number): number {
  const n = p.cum.length;
  if (n < 2) return 0;
  let lo = 0, hi = n - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (p.cum[mid] <= s) lo = mid; else hi = mid;
  }
  return lo;
}

/** Shortest signed difference b − a, wrapped to ±π. */
export function wrapAngle(a: number): number {
  let x = a;
  while (x > Math.PI) x -= Math.PI * 2;
  while (x < -Math.PI) x += Math.PI * 2;
  return x;
}

/**
 * The wind-correction angle: the craft points into the wind by this much so its
 * TRACK is the course. WCA = asin((w / v) · sin(wind − course)).
 */
export function crabAngle(p: FlightPlan, track: number, speed: number): number {
  if (p.windSpeed <= 0 || speed <= 1e-3) return 0;
  const r = Math.max(-0.95, Math.min(0.95, (p.windSpeed / speed) * Math.sin(p.windFrom - track)));
  return Math.asin(r);
}

/** Everything about the craft at arc length `s`. */
export function flightAt(p: FlightPlan, s: number): FlightPose {
  const n = p.cum.length;
  const sc = Math.max(0, Math.min(p.length, s));
  if (n < 2) {
    return { x: p.pts[0] ?? 0, z: p.pts[1] ?? 0, alt: 0, track: 0, yaw: 0, bank: 0, pitch: 0, speed: p.vEnd, done: true };
  }
  const i = segAt(p, sc);
  const segLen = Math.max(1e-9, p.cum[i + 1] - p.cum[i]);
  const f = Math.max(0, Math.min(1, (sc - p.cum[i]) / segLen));
  const x = p.pts[i * 2] + (p.pts[(i + 1) * 2] - p.pts[i * 2]) * f;
  const z = p.pts[i * 2 + 1] + (p.pts[(i + 1) * 2 + 1] - p.pts[i * 2 + 1]) * f;

  // Track: blend across the vertex so the heading is continuous through a fillet.
  const y0 = p.yaws[i];
  const y1 = p.yaws[Math.min(p.yaws.length - 1, i + 1)];
  const track = y0 + wrapAngle(y1 - y0) * f;

  const speed = speedAt(p, sc);
  // Curvature from the heading change per metre, then the bank that flies it.
  const kappa = wrapAngle(y1 - y0) / segLen;
  const bankMax = BANK_MAX_DEG * DEG;
  const bank = Math.max(-bankMax, Math.min(bankMax, Math.atan((speed * speed * kappa) / G)));

  const alt = altAt(p, sc);
  // Pitch from the vertical gradient, over a metre either side — and over the
  // span ACTUALLY sampled, so the clamp at either end does not halve it.
  const h = 1;
  const hi = Math.min(p.length, sc + h), lo = Math.max(0, sc - h);
  const pitch = p.air && hi > lo ? Math.atan2(altAt(p, hi) - altAt(p, lo), hi - lo) : 0;

  return { x, z, alt, track, yaw: track + crabAngle(p, track, speed), bank, pitch, speed, done: sc >= p.length - 1e-6 };
}

/** Advance along the plan for `dt` seconds, integrating the speed profile (two steps — it changes as you go). */
export function advanceFlight(p: FlightPlan, s: number, dt: number): number {
  if (dt <= 0) return s;
  const half = dt / 2;
  const s1 = s + speedAt(p, s) * half;
  const s2 = s1 + speedAt(p, Math.min(p.length, s1)) * half;
  return Math.max(0, Math.min(p.length, s2));
}

/**
 * Where a position sits along the plan. The server writes only x/z, so BOTH
 * sides recover `s` this way rather than carrying it — no new column, and the
 * client's walk cannot drift from the server's.
 */
export function progressAt(p: FlightPlan, x: number, z: number): number {
  const n = p.cum.length;
  if (n < 2) return 0;
  let best = 0, bestD = Infinity;
  for (let i = 0; i + 1 < n; i++) {
    const axp = p.pts[i * 2], azp = p.pts[i * 2 + 1];
    const bxp = p.pts[(i + 1) * 2], bzp = p.pts[(i + 1) * 2 + 1];
    const sx = bxp - axp, sz = bzp - azp;
    const l2 = sx * sx + sz * sz;
    const t = l2 > 1e-12 ? Math.max(0, Math.min(1, ((x - axp) * sx + (z - azp) * sz) / l2)) : 0;
    const px = axp + sx * t, pz = azp + sz * t;
    const d = (x - px) * (x - px) + (z - pz) * (z - pz);
    if (d < bestD) { bestD = d; best = p.cum[i] + Math.sqrt(l2) * t; }
  }
  return best;
}
